import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { createProperty, getAddressById, lookupAddress } from "../api/propertyApi";

import {
  Box,
  TextField,
  Button,
  Alert,
  Typography,
  Paper,
  Stack,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

function CreatePropertyPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    property_type: "",
    address_line1: "",
    address_line2: "",
    city: "",
    county: "",
    postcode: "",
    property_manager: "",
    freeholder_name: "",
    freeholder_address: "",
    rtm_rmc_type: "",
    rtm_rmc_name: "",
    company_number: "",
    utr_number: "",
    bank_name: "",
    account_number: "",
    sort_code: "",
    ideal_postcodes_id: "",
    uprn: "",
    address_latitude: "",
    address_longitude: "",
    live: false, // New properties default to non-live
  });

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);
  const [lookupPostcode, setLookupPostcode] = useState("");
  const [lookupFilter, setLookupFilter] = useState("");
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [selectedLookup, setSelectedLookup] = useState("");
  const hasRtmRmc = Boolean(form.rtm_rmc_type);

  // Handle input change
  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleManagementStructureChange = (e) => {
    const value = e.target.value === "Neither" ? "" : e.target.value;
    setForm((prev) => ({
      ...prev,
      rtm_rmc_type: value,
      rtm_rmc_name: value ? prev.rtm_rmc_name : "",
    }));
  };

  const handleLookup = async () => {
    setLookupError("");
    setLookupResults([]);
    setSelectedLookup("");
    const trimmed = lookupPostcode.trim();
    if (!trimmed) {
      setLookupError("Enter a postcode to search.");
      return;
    }
    setLookupLoading(true);
    try {
      const data = await lookupAddress(trimmed, lookupFilter.trim());
      const suggestions = data?.suggestions || [];
      setLookupResults(suggestions);
      if (!suggestions.length) {
        setLookupError("No addresses found for that postcode.");
      }
    } catch (err) {
      console.error("Address lookup error:", err);
      setLookupError(err?.message || "Address lookup failed.");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSelectLookup = async (value) => {
    setSelectedLookup(value);
    setLookupError("");
    const selected = lookupResults[Number(value)] || null;
    if (!selected?.id) return;
    setForm((prev) => ({
      ...prev,
      address_line1: "",
      address_line2: "",
      city: "",
      county: "",
      postcode: "",
      ideal_postcodes_id: "",
      uprn: "",
      address_latitude: "",
      address_longitude: "",
    }));
    try {
      const address = await getAddressById(selected.id);
      setForm((prev) => ({
        ...prev,
        address_line1: address.line_1 || prev.address_line1,
        address_line2: address.line_2 || prev.address_line2,
        city: address.town_or_city || address.locality || prev.city,
        county: address.county || prev.county,
        postcode: address.postcode || lookupPostcode.trim() || prev.postcode,
        ideal_postcodes_id: address.ideal_postcodes_id || selected.id,
        uprn: address.uprn || "",
        address_latitude: address.latitude ?? "",
        address_longitude: address.longitude ?? "",
      }));
    } catch (err) {
      console.error("Address get error:", err);
      setLookupError(err?.message || "Address lookup failed.");
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...form,
        ideal_postcodes_id: form.ideal_postcodes_id || null,
        uprn: form.uprn || null,
        address_latitude: form.address_latitude || null,
        address_longitude: form.address_longitude || null,
      };
      await createProperty(payload);
      navigate("/properties", {
        state: {
          toast: {
            message: "Property created",
            severity: "success",
          },
        },
      });
    } catch (err) {
      console.error("Create property error:", err);
      setToast({
        open: true,
        message:
          err?.response?.data?.error ||
          err?.message ||
          "Failed to create property. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Box className="page-container">
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Create New Property
        </Typography>

        <Paper
          elevation={2}
          sx={{
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                disabled={loading}
                fullWidth
              />

              <FormControl fullWidth required disabled={loading}>
                <InputLabel>Property Type</InputLabel>
                <Select
                  name="property_type"
                  value={form.property_type}
                  onChange={handleChange}
                  label="Property Type"
                >
                  <MenuItem value="Block">Block</MenuItem>
                  <MenuItem value="Estate">Estate</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Address Lookup
              </Typography>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                >
                  <TextField
                    label="Postcode"
                    value={lookupPostcode}
                    onChange={(e) => setLookupPostcode(e.target.value)}
                    disabled={loading || lookupLoading}
                    fullWidth
                  />
                  <TextField
                    label="Filter (optional)"
                    value={lookupFilter}
                    onChange={(e) => setLookupFilter(e.target.value)}
                    disabled={loading || lookupLoading}
                    fullWidth
                  />
                </Stack>
                <Button
                  variant="outlined"
                  onClick={handleLookup}
                  disabled={loading || lookupLoading}
                  startIcon={<SearchIcon />}
                  sx={{ alignSelf: "flex-start", minWidth: 180 }}
                >
                  {lookupLoading ? "Searching..." : "Find Address"}
                </Button>
                {lookupError && (
                  <Alert severity="error">{lookupError}</Alert>
                )}
                <Collapse in={lookupResults.length > 0} timeout={300}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {lookupResults.length}{" "}
                    {lookupResults.length === 1 ? "address" : "addresses"} found.
                    Choose one from the dropdown.
                  </Typography>
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel>Select Address</InputLabel>
                    <Select
                      value={selectedLookup}
                      label="Select Address"
                      onChange={(e) => handleSelectLookup(e.target.value)}
                    >
                      {lookupResults.map((addr, index) => {
                        const label =
                          addr.address ||
                          [addr.id]
                            .filter(Boolean)
                            .join(", ");
                        return (
                          <MenuItem key={`${index}-${label}`} value={String(index)}>
                            {label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Collapse>
              </Stack>

              {[
                { label: "Address Line 1", name: "address_line1", required: true },
                { label: "Address Line 2", name: "address_line2" },
                { label: "City", name: "city", required: true },
                { label: "County", name: "county" },
                { label: "Postcode", name: "postcode", required: true },
                { label: "Property Manager", name: "property_manager" },
                { label: "Freeholder Name", name: "freeholder_name" },
                { label: "Company Number", name: "company_number" },
                { label: "UTR Number", name: "utr_number" },
                { label: "Bank Name", name: "bank_name" },
                { label: "Account Number", name: "account_number" },
                { label: "Sort Code", name: "sort_code" },
              ].map(({ label, name, required }) => (
                <TextField
                  key={name}
                  label={label}
                  name={name}
                  value={form[name]}
                  onChange={handleChange}
                  required={required}
                  disabled={loading}
                  fullWidth
                />
              ))}

              <TextField
                label="Freeholder Address"
                name="freeholder_address"
                value={form.freeholder_address}
                onChange={handleChange}
                disabled={loading}
                fullWidth
                multiline
                minRows={3}
              />

              <FormControl fullWidth required disabled={loading}>
                <InputLabel>Management Structure</InputLabel>
                <Select
                  value={form.rtm_rmc_type || "Neither"}
                  onChange={handleManagementStructureChange}
                  label="Management Structure"
                >
                  <MenuItem value="Neither">Neither</MenuItem>
                  <MenuItem value="RTM">RTM</MenuItem>
                  <MenuItem value="RMC">RMC</MenuItem>
                </Select>
              </FormControl>

              <Collapse in={hasRtmRmc} timeout={300} unmountOnExit>
                <Stack spacing={2}>
                  <TextField
                    label={`Name of the ${form.rtm_rmc_type}`}
                    name="rtm_rmc_name"
                    value={form.rtm_rmc_name}
                    onChange={handleChange}
                    required={hasRtmRmc}
                    disabled={loading}
                    fullWidth
                  />
                </Stack>
              </Collapse>

              <FormControlLabel
                control={
                  <Switch
                    checked={form.live}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, live: e.target.checked }))
                    }
                    disabled={loading}
                  />
                }
                label="Live"
              />

              <Button
                variant="contained"
                size="large"
                type="submit"
                disabled={loading}
                sx={{ mt: 1 }}
              >
                {loading ? "Creating..." : "Create Property"}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default CreatePropertyPage;
